import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import MicroqualificationGroupsService from "../../services/MicroqualificationGroupsService";

let initialState = {
    // Groups
    groups: [],
    currentGroup: null,
    groupsLoading: false,
    groupsError: null,

    // Students
    students: [],
    studentsLoading: false,
    studentsError: null,

    // Grades
    disciplineGrades: [],
    gradesLoading: false,
    gradesError: null,
}

// ==================== GROUPS THUNKS ====================

export const getAllGroups = createAsyncThunk(
    'microqualificationGroups/getAllGroups',
    async (_, {rejectWithValue}) => {
        try {
            const response = await MicroqualificationGroupsService.getAllGroups();
            return response.data;
        } catch (error) {
            return rejectWithValue(error?.response?.data);
        }
    }
);

export const getGroupById = createAsyncThunk(
    'microqualificationGroups/getGroupById',
    async (groupId, {rejectWithValue}) => {
        try {
            const response = await MicroqualificationGroupsService.getGroupById(groupId);
            return response.data;
        } catch (error) {
            return rejectWithValue(error?.response?.data);
        }
    }
);

export const createGroup = createAsyncThunk(
    'microqualificationGroups/createGroup',
    async (groupData, {rejectWithValue}) => {
        try {
            const response = await MicroqualificationGroupsService.createGroup(groupData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error?.response?.data);
        }
    }
);

export const updateGroup = createAsyncThunk(
    'microqualificationGroups/updateGroup',
    async ({groupId, groupData}, {rejectWithValue}) => {
        try {
            const response = await MicroqualificationGroupsService.updateGroup(groupId, groupData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error?.response?.data);
        }
    }
);

export const deleteGroup = createAsyncThunk(
    'microqualificationGroups/deleteGroup',
    async (groupId, {rejectWithValue}) => {
        try {
            await MicroqualificationGroupsService.deleteGroup(groupId);
            return groupId;
        } catch (error) {
            return rejectWithValue(error?.response?.data);
        }
    }
);

// ==================== STUDENTS THUNKS ====================

export const getGroupStudents = createAsyncThunk(
    'microqualificationGroups/getGroupStudents',
    async (groupId, {rejectWithValue}) => {
        try {
            const response = await MicroqualificationGroupsService.getGroupStudents(groupId);
            return response.data;
        } catch (error) {
            return rejectWithValue(error?.response?.data);
        }
    }
);

export const addStudentsToGroup = createAsyncThunk(
    'microqualificationGroups/addStudentsToGroup',
    async ({groupId, studentIds}, {rejectWithValue}) => {
        try {
            const response = await MicroqualificationGroupsService.addStudentsToGroup(groupId, studentIds);
            return response.data;
        } catch (error) {
            return rejectWithValue(error?.response?.data);
        }
    }
);

// Removed updateStudent - students are now managed through User entity

export const removeStudentFromGroup = createAsyncThunk(
    'microqualificationGroups/removeStudentFromGroup',
    async ({groupId, studentId}, {rejectWithValue}) => {
        try {
            await MicroqualificationGroupsService.removeStudentFromGroup(groupId, studentId);
            return studentId;
        } catch (error) {
            return rejectWithValue(error?.response?.data);
        }
    }
);

// ==================== GRADES THUNKS ====================

export const getDisciplineGrades = createAsyncThunk(
    'microqualificationGroups/getDisciplineGrades',
    async ({groupId, disciplineId}, {rejectWithValue}) => {
        try {
            const response = await MicroqualificationGroupsService.getDisciplineGrades(groupId, disciplineId);
            return response.data;
        } catch (error) {
            return rejectWithValue(error?.response?.data);
        }
    }
);

export const batchAddGrades = createAsyncThunk(
    'microqualificationGroups/batchAddGrades',
    async ({groupId, disciplineId, gradesData}, {rejectWithValue}) => {
        try {
            const response = await MicroqualificationGroupsService.batchAddGrades(groupId, disciplineId, gradesData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error?.response?.data);
        }
    }
);

// ==================== SLICE ====================

const microqualificationGroupsSlice = createSlice({
    name: 'microqualificationGroups',
    initialState,
    reducers: {
        clearCurrentGroup: (state) => {
            state.currentGroup = null;
        },
        clearStudents: (state) => {
            state.students = [];
        },
        clearGrades: (state) => {
            state.disciplineGrades = [];
        },
        clearErrors: (state) => {
            state.groupsError = null;
            state.studentsError = null;
            state.gradesError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // ==================== GET ALL GROUPS ====================
            .addCase(getAllGroups.pending, (state) => {
                state.groupsLoading = true;
                state.groupsError = null;
            })
            .addCase(getAllGroups.fulfilled, (state, action) => {
                state.groupsLoading = false;
                state.groups = action.payload;
            })
            .addCase(getAllGroups.rejected, (state, action) => {
                state.groupsLoading = false;
                state.groupsError = action.payload;
            })
            // ==================== GET GROUP BY ID ====================
            .addCase(getGroupById.pending, (state) => {
                state.groupsLoading = true;
                state.groupsError = null;
            })
            .addCase(getGroupById.fulfilled, (state, action) => {
                state.groupsLoading = false;
                state.currentGroup = action.payload;
            })
            .addCase(getGroupById.rejected, (state, action) => {
                state.groupsLoading = false;
                state.groupsError = action.payload;
            })
            // ==================== CREATE GROUP ====================
            .addCase(createGroup.pending, (state) => {
                state.groupsLoading = true;
                state.groupsError = null;
            })
            .addCase(createGroup.fulfilled, (state, action) => {
                state.groupsLoading = false;
                state.groups.push(action.payload);
            })
            .addCase(createGroup.rejected, (state, action) => {
                state.groupsLoading = false;
                state.groupsError = action.payload;
            })
            // ==================== UPDATE GROUP ====================
            .addCase(updateGroup.pending, (state) => {
                state.groupsLoading = true;
                state.groupsError = null;
            })
            .addCase(updateGroup.fulfilled, (state, action) => {
                state.groupsLoading = false;
                state.currentGroup = action.payload;
                const index = state.groups.findIndex(g => g.id === action.payload.id);
                if (index !== -1) {
                    state.groups[index] = action.payload;
                }
            })
            .addCase(updateGroup.rejected, (state, action) => {
                state.groupsLoading = false;
                state.groupsError = action.payload;
            })
            // ==================== DELETE GROUP ====================
            .addCase(deleteGroup.pending, (state) => {
                state.groupsLoading = true;
                state.groupsError = null;
            })
            .addCase(deleteGroup.fulfilled, (state, action) => {
                state.groupsLoading = false;
                state.groups = state.groups.filter(g => g.id !== action.payload);
            })
            .addCase(deleteGroup.rejected, (state, action) => {
                state.groupsLoading = false;
                state.groupsError = action.payload;
            })
            // ==================== GET STUDENTS ====================
            .addCase(getGroupStudents.pending, (state) => {
                state.studentsLoading = true;
                state.studentsError = null;
            })
            .addCase(getGroupStudents.fulfilled, (state, action) => {
                state.studentsLoading = false;
                state.students = action.payload;
            })
            .addCase(getGroupStudents.rejected, (state, action) => {
                state.studentsLoading = false;
                state.studentsError = action.payload;
            })
            // ==================== ADD STUDENTS (BATCH) ====================
            .addCase(addStudentsToGroup.pending, (state) => {
                state.studentsLoading = true;
                state.studentsError = null;
            })
            .addCase(addStudentsToGroup.fulfilled, (state, action) => {
                state.studentsLoading = false;
                // Refresh students list after batch add
                // Backend returns success message, we'll refetch the list
            })
            .addCase(addStudentsToGroup.rejected, (state, action) => {
                state.studentsLoading = false;
                state.studentsError = action.payload;
            })
            // ==================== REMOVE STUDENT ====================
            .addCase(removeStudentFromGroup.pending, (state) => {
                state.studentsLoading = true;
                state.studentsError = null;
            })
            .addCase(removeStudentFromGroup.fulfilled, (state, action) => {
                state.studentsLoading = false;
                state.students = state.students.filter(s => s.id !== action.payload);
            })
            .addCase(removeStudentFromGroup.rejected, (state, action) => {
                state.studentsLoading = false;
                state.studentsError = action.payload;
            })
            // ==================== GET DISCIPLINE GRADES ====================
            .addCase(getDisciplineGrades.pending, (state) => {
                state.gradesLoading = true;
                state.gradesError = null;
            })
            .addCase(getDisciplineGrades.fulfilled, (state, action) => {
                state.gradesLoading = false;
                state.disciplineGrades = action.payload;
            })
            .addCase(getDisciplineGrades.rejected, (state, action) => {
                state.gradesLoading = false;
                state.gradesError = action.payload;
            })
            // ==================== BATCH ADD GRADES ====================
            .addCase(batchAddGrades.pending, (state) => {
                state.gradesLoading = true;
                state.gradesError = null;
            })
            .addCase(batchAddGrades.fulfilled, (state, action) => {
                state.gradesLoading = false;
                state.disciplineGrades = action.payload;
            })
            .addCase(batchAddGrades.rejected, (state, action) => {
                state.gradesLoading = false;
                state.gradesError = action.payload;
            });
    }
});

export const {clearCurrentGroup, clearStudents, clearGrades, clearErrors} = microqualificationGroupsSlice.actions;

export default microqualificationGroupsSlice.reducer;
